import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import type { ApiResponse } from '@/utils/api/user-service/models'
import { zodResolver } from '@hookform/resolvers/zod'
import { ArrowRight, Check, Eye, EyeOff, Lock, Mail, User } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { useSignUp } from '../api/useSignUp'
import { useAuth } from './auth-provider'
import { useNavigate } from '@tanstack/react-router'

const schema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Please enter a valid email address'),
  password: z.string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number'),
  confirmPassword: z.string(),
  acceptTerms: z.boolean().refine(val => val === true, {
    message: 'You must accept the terms and conditions'
  })
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
})

type FormData = z.infer<typeof schema>

export function SignUpForm() {
  const { /* login */ } = useAuth()
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const navigate = useNavigate()
  const signUp = useSignUp()
  

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      acceptTerms: false
    },
    mode: 'onChange'
  })

  const watchPassword = form.watch('password')

  const passwordRequirements = [
    { label: 'At least 8 characters', test: (pwd: string) => pwd.length >= 8 },
    { label: 'One uppercase letter', test: (pwd: string) => /[A-Z]/.test(pwd) },
    { label: 'One lowercase letter', test: (pwd: string) => /[a-z]/.test(pwd) },
    { label: 'One number', test: (pwd: string) => /[0-9]/.test(pwd) },
  ]

  async function onSubmit(values: FormData) {
    try {
      const parsed = schema.parse(values)
      console.log("Submitting signup form with values:", parsed);
      
      const res = await signUp.mutateAsync({
        firstName: parsed.firstName,
        lastName: parsed.lastName,
        email: parsed.email,
        password: parsed.password,
      })
      // alert("22Signup response:" + JSON.stringify(res));
      console.log("Signup response:", res);
      
      // Consider ApiResponse.success when available
      const apiRes = res.data as ApiResponse
      if (apiRes?.success === false) {
        throw new Error(apiRes.message || 'Failed to create account')
      }
      const message = apiRes?.message || 'Account created successfully. Please sign in.'
      toast.success(message)

      // Redirect to login; preserve next if present via query param on current URL
      const url = new URL(window.location.href)
      const redirect = url.searchParams.get('redirect') || '/login'
      // If redirect already points to /login, just go there; otherwise add redirect target to login
      if (redirect === '/login') {
        navigate({ to: '/login' })
      } else {
        navigate({ to: '/login', search: { redirect } as any })
      }
    } catch (err) {
      console.log("Signup error:", err);
      
      const message = (err as any)?.response?.data?.message || (err as Error)?.message || 'Failed to create account'
      toast.error(message)
    }
  }

  return (
    <Card className="w-full max-w-md mx-auto shadow-xl border-0 bg-card/50 backdrop-blur-sm">
      <CardHeader className="space-y-2 pb-2">

        <div className="flex items-center justify-center space-x-4">
          <div className="text-center space-y-2">
            <CardTitle className="text-2xl font-bold">Create account</CardTitle>
            <CardDescription className="text-muted-foreground">
              Join UIBFS Membership System today
            </CardDescription>
          </div>
        </div>

      </CardHeader>

      <CardContent className="space-y-6">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                name="firstName"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required className="text-sm font-medium">First Name</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                        <Input
                          placeholder="John"
                          autoComplete="given-name"
                          className="pl-10 h-11"
                          {...field}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                name="lastName"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required className="text-sm font-medium">Last Name</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                        <Input
                          placeholder="Doe"
                          autoComplete="family-name"
                          className="pl-10 h-11"
                          {...field}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              name="email"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel required className="text-sm font-medium">Email Address</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                      <Input
                        type="email"
                        placeholder="john.doe@example.com"
                        autoComplete="email"
                        className="pl-10 h-11"
                        {...field}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              name="password"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel required className="text-sm font-medium">Password</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                      <Input
                        type={showPassword ? "text" : "password"}
                        placeholder="Create a strong password"
                        autoComplete="new-password"
                        className="pl-10 pr-10 h-11"
                        {...field}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <Eye className="h-4 w-4 text-muted-foreground" />
                        )}
                      </Button>
                    </div>
                  </FormControl>

                  {watchPassword && (
                    <div className="mt-2 space-y-1">
                      {passwordRequirements.map((req, index) => (
                        <div key={index} className="flex items-center space-x-2 text-xs">
                          <div className={`w-3 h-3 rounded-full flex items-center justify-center ${req.test(watchPassword)
                            ? 'bg-green-500'
                            : 'bg-gray-300'
                            }`}>
                            {req.test(watchPassword) && (
                              <Check className="w-2 h-2 text-white" />
                            )}
                          </div>
                          <span className={req.test(watchPassword) ? 'text-green-600' : 'text-muted-foreground'}>
                            {req.label}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              name="confirmPassword"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel required className="text-sm font-medium">Confirm Password</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                      <Input
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Confirm your password"
                        autoComplete="new-password"
                        className="pl-10 pr-10 h-11"
                        {...field}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <Eye className="h-4 w-4 text-muted-foreground" />
                        )}
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              name="acceptTerms"
              control={form.control}
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <div className="flex items-center gap-3">
                    <FormControl>
                      <Checkbox id="terms"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        className='border-2 focus:ring-2 border-primary'
                      />
                    </FormControl>
                    <Label htmlFor="terms" className="text-sm font-normal">
                      I agree to the&nbsp;
                      <Button variant="link" className="px-0 h-auto text-sm font-semibold text-primary">
                        Terms of Service
                      </Button>
                      &nbsp;&&nbsp;
                      <Button variant="link" className="px-0 h-auto text-sm font-semibold text-primary">
                        Privacy Policy
                      </Button>
                      <span aria-hidden="true" className="ml-1 text-destructive">*</span>
                      <span className="sr-only"> (required)</span>
                    </Label>
                  </div>
                </FormItem>
              )}
            />

            <Button
              type="submit"
              className="w-full h-11 rounded-lg bg-gradient-to-r from-primary to-[color-mix(in_oklab,var(--primary),white_18%)] text-primary-foreground font-semibold shadow-lg hover:from-[color-mix(in_oklab,var(--primary),white_8%)] hover:to-[color-mix(in_oklab,var(--primary),white_28%)] focus-visible:ring-2 focus-visible:ring-primary transition-colors"
              disabled={signUp.isPending}
            >
              {signUp.isPending ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Creating account...</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <span>Create account</span>
                  <ArrowRight className="w-4 h-4" />
                </div>
              )}
            </Button>
          </form>
        </Form>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <Separator className="w-full" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-card px-2 text-muted-foreground">Or continue with</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Button variant="outline" className="h-11">
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Google
          </Button>
          <Button variant="outline" className="h-11">
            <svg className="w-4 h-4 mr-2" viewBox="0 0 48 48" fill="currentColor" aria-hidden="true">
              <path d="M28 12h12a4 4 0 0 1 4 4v16a4 4 0 0 1-4 4H28V12z" />
              <path d="M4 16a4 4 0 0 1 4-4h18v24H8a4 4 0 0 1-4-4V16z" opacity=".7" />
              <path d="M10.5 18.5h0a1.5 1.5 0 0 0-1.5 1.5v8a1.5 1.5 0 0 0 1.5 1.5h0A9.5 9.5 0 0 0 20 20a9.5 9.5 0 0 0-9.5-1.5z" fill="currentColor" />
              <path d="M8 18l10 7 10-7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
            </svg>
            Outlook
          </Button>
        </div>

        <div className="text-center text-sm text-muted-foreground">
          Already have an account?{' '}
          <Button variant="link" className="px-0 h-auto font-semibold text-primary" asChild>
            <a href="/login">Sign in</a>
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
